import { useState, useEffect, useCallback } from "react";
import { LoaderCircle } from "lucide-react";
import API_BASE_URL from "../../config";
import useJobs from "../../hooks/useJobs";
import useJobWaiter from "../../hooks/useJobWaiter";
import DataTable from "./DataTable";
import DocumentPicker from "./DocumentPicker";
import { stripUuid } from "./complianceExport";
import { productSheetToMarkdown, downloadText } from "./productSheetExport";
import "./CompliancePipeline.css";
import "./ProductSheets.css";

export default function ProductSheets({ database }) {
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [activeVariant, setActiveVariant] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState(null);
  const { startJob } = useJobs({ enabled: false }); // only need startJob; no polling here
  const { waitFor } = useJobWaiter();

  useEffect(() => {
    const qs = database ? `?database=${encodeURIComponent(database)}` : "";
    fetch(`${API_BASE_URL}/api/v1/approval/z-documents${qs}`)
      .then((r) => (r.ok ? r.json() : { documents: [] }))
      .then((d) => setDocs(d.documents || []))
      .catch(() => setDocs([]));
  }, [database]);

  const buildCatalog = useCallback(async () => {
    if (!docId) return;
    setBusy("catalog"); setError(null); setCatalog(null); setSheet(null); setActiveVariant("");
    try {
      const data = await startJob("/api/v1/approval/products", {
        document_id: docId, database: database || "Betonbau",
      });
      if (!data.job_id) { setCatalog(data.result); return; }  // synchronous fallback (jobs disabled)
      const job = await waitFor(data.job_id);
      if (job.status === "failed") throw new Error(job.error || "unbekannt");
      setCatalog(job.result);
    } catch (e) { setError(`Katalog fehlgeschlagen: ${e.message}`); }
    finally { setBusy(""); }
  }, [docId, database, startJob, waitFor]);

  const openSheet = useCallback(async (variant) => {
    setActiveVariant(variant.variant_id); setBusy("sheet"); setError(null); setSheet(null);
    try {
      const data = await startJob("/api/v1/approval/products/sheet", {
        document_id: docId, variant_id: variant.variant_id, database: database || "Betonbau",
      });
      if (!data.job_id) { setSheet(data.result); return; }  // synchronous fallback (jobs disabled)
      const job = await waitFor(data.job_id);
      if (job.status === "failed") throw new Error(job.error || "unbekannt");
      setSheet(job.result);
    } catch (e) { setError(`Datenblatt fehlgeschlagen: ${e.message}`); }
    finally { setBusy(""); }
  }, [docId, database, startJob, waitFor]);

  const unverified = sheet
    ? sheet.sections.reduce((n, s) => n + s.entries.filter((e) => e.verified === false).length, 0)
    : 0;

  return (
    <div className="ps-root">
      <div className="ds-toolbar cp-controls">
        <DocumentPicker
          id="ps-doc"
          label="Document"
          placeholder="— select an approval document —"
          documents={docs}
          value={docId}
          onChange={(id) => { setDocId(id); setCatalog(null); setSheet(null); }}
        />
        <button className="ds-btn ds-btn--primary" onClick={buildCatalog} disabled={!docId || busy === "catalog"}>
          {busy === "catalog" ? "Building…" : "1 · Build catalogue"}
        </button>
      </div>

      {error && <div className="ds-alert ds-alert--danger ds-flush">{error}</div>}

      {catalog && (
        <div className="ps-body">
          <div className="ps-catalog" role="group" aria-label="Produktvarianten">
            <div className="ps-catalog-head">
              {stripUuid(catalog.document_name)} · {catalog.approval_number || "—"}
            </div>
            {(catalog.variants || []).length === 0 && <p className="ds-help cp-hint">No product variants detected.</p>}
            {(catalog.variants || []).map((v) => (
              <button key={v.variant_id} type="button"
                className="ds-chip ps-variant"
                aria-pressed={activeVariant === v.variant_id}
                onClick={() => openSheet(v)}>
                {v.label || v.designation}
              </button>
            ))}
          </div>

          <div className="ps-sheet">
            {busy === "sheet" && (
              <p className="ds-help cp-hint ps-busy">
                <LoaderCircle size={14} className="ds-spin" aria-hidden="true" />Assembling datasheet…
              </p>
            )}
            {sheet && (
              <>
                <div className="ps-sheet-head">
                  <div className="ps-sheet-title">{sheet.variant.designation} · {sheet.variant.size}</div>
                  <div className="ds-meta">
                    RDK {sheet.variant.rohdichteklasse || "—"} · DFK {sheet.variant.druckfestigkeitsklasse || "—"}
                    {" · "}{sheet.approval_number || ""}
                    {sheet.valid_until ? ` · gültig bis ${sheet.valid_until}` : ""}
                  </div>
                  {unverified > 0 && (
                    <span className="ds-badge ds-badge--warning">⚠ {unverified} Eintrag/Einträge nicht verifiziert</span>
                  )}
                  <div>
                    <button className="ds-btn ds-btn--secondary ds-btn--sm"
                      onClick={() => downloadText(`${sheet.variant.designation || "produkt"}.md`, productSheetToMarkdown(sheet))}>
                      ⬇ Markdown
                    </button>
                  </div>
                </div>
                {sheet.sections.map((s, i) => (
                  <div key={i} className="ps-section">
                    <h4 className="cp-section-head">{s.title || s.key}</h4>
                    <DataTable>
                      <tbody>
                        {s.entries.map((e, j) => (
                          <tr key={j} className={e.verified === false ? "ps-row-unverified" : ""}>
                            <td className="ps-e-label">{e.label}</td>
                            <td className="ps-e-value">
                              {e.value ?? "—"}
                              {e.verified === false && <span className="ds-badge ds-badge--warning cp-badge" title="nicht im Quelltext gefunden">⚠ prüfen</span>}
                            </td>
                            <td className="ps-e-norm">{e.norm || ""}</td>
                            <td className="ps-e-src">{e.source?.location || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
